import React from "react";

/**
 * TotoHeadAndNeck — a stylized SVG silhouette of Toto's head and upper neck.
 *
 * Reads as a Westie at small scale: rounded head, two upright pointed ears,
 * short muzzle with a small dark nose, two calm eyes. Upper neck fills into
 * the base of the frame so Toto is anchored, not floating.
 *
 * The two eyes are marked with `.toto-house__presence-eye` classes so the
 * CSS blink animation can find them.
 *
 * Deliberately low-detail — the house's material and depth do the heavy
 * lifting. Toto is a presence, not a photograph.
 */
export default function TotoHeadAndNeck() {
  return (
    <svg
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Toto"
      style={{ display: "block", width: "100%", height: "auto" }}
    >
      <defs>
        {/* Soft edge suggests fur */}
        <filter id="totoFurSoft" x="-8%" y="-8%" width="116%" height="116%">
          <feGaussianBlur stdDeviation="0.35" />
        </filter>
        {/* Gentle inner shading for the fur body */}
        <radialGradient id="totoFur" cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#FBF9F5" />
          <stop offset="70%" stopColor="#EFEBE3" />
          <stop offset="100%" stopColor="#DED8CB" />
        </radialGradient>
        {/* Subtle darker gradient inside the ears */}
        <linearGradient id="totoEarInner" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C7BEA9" />
          <stop offset="100%" stopColor="#E7DFCC" />
        </linearGradient>
      </defs>

      {/* Upper neck — flows into the frame line at the bottom */}
      <path
        d="M 30 92 Q 32 74 42 68 L 58 68 Q 68 74 70 92 Z"
        fill="url(#totoFur)"
        filter="url(#totoFurSoft)"
      />

      {/* Head — rounded with a slight downward-forward tilt */}
      <path
        d="M 20 52
           C 20 35 32 26 50 26
           C 68 26 80 35 80 52
           C 80 66 70 76 60 78
           L 40 78
           C 30 76 20 66 20 52 Z"
        fill="url(#totoFur)"
        filter="url(#totoFurSoft)"
      />

      {/* Left ear — upright, pointed (Westie signature) */}
      <path
        d="M 28 30 L 24 12 L 38 22 Z"
        fill="url(#totoEarInner)"
        filter="url(#totoFurSoft)"
      />
      {/* Right ear */}
      <path
        d="M 72 30 L 76 12 L 62 22 Z"
        fill="url(#totoEarInner)"
        filter="url(#totoFurSoft)"
      />

      {/* Muzzle — short, subtly lighter to catch the interior bloom */}
      <path
        d="M 38 60 Q 50 68 62 60 Q 62 72 50 74 Q 38 72 38 60 Z"
        fill="#F6F2EA"
        opacity="0.85"
      />

      {/* Nose — small dark triangle */}
      <path
        d="M 46 62 L 54 62 L 50 67 Z"
        fill="#1F1D1A"
      />

      {/* Eyes — two calm dark ovals with the blink class */}
      <ellipse
        className="toto-eye toto-eye--left"
        cx="39" cy="49" rx="2.4" ry="3.2"
        fill="#1F1D1A"
      />
      <ellipse
        className="toto-eye toto-eye--right"
        cx="61" cy="49" rx="2.4" ry="3.2"
        fill="#1F1D1A"
      />

      {/* Eye catchlight — a tiny warm dot picks up the interior bloom */}
      <circle cx="39.7" cy="47.9" r="0.5" fill="#FFF8E7" opacity="0.9" />
      <circle cx="61.7" cy="47.9" r="0.5" fill="#FFF8E7" opacity="0.9" />
    </svg>
  );
}
